/**
 * EQUIPMENT IMPORT SOURCES — the one resolution ladder that brings equipment
 * from the site's Equipment BOM, the OEM source register, and the platform
 * equipment catalog into the G&I equipment selection.
 *
 * Ladder (strongest tier wins):
 *  a. `catalog-envelope` — the item's OEM product is admitted for
 *     'generation-impact' (or points at a shipped screening envelope) → offer
 *     the REAL version-locked envelope; numbers stay in the shipped catalog.
 *  b. `prefill-full`   — published output + mappable scope + heat rate (or a
 *     labeled derivation) → prefill the Add-equipment form.
 *  c. `prefill-partial` — identity + per-unit MW only → prefill what exists;
 *     the form's validation guides the rest (heat rate stays blank).
 *  d. `insufficient`   — explain why; the blank form remains available.
 *
 * Solver-integrity rules: no tier ever invents heat rate, auxiliary, ambient,
 * or degradation values; derived heat rates and unstated heating-value bases
 * are flagged so the dialog forces an explicit user choice; user entries never
 * gain an OEM basis. Pure and deterministic throughout.
 */
import type {
  GenerationEquipmentRatingScope,
  GenerationEquipmentWorkspace,
  GenerationUserEquipmentEntry,
  GenerationUserEquipmentImportSource,
} from '../../types/generationImpact';
import type {
  OemGenerationPerformanceCase,
  OemGenerationRatingScope,
  OemGenerationSourceRegister,
  OemGenerationTechnology,
} from '../../types/oemGenerationCatalog';
import {
  isGenerationBomItem,
  selectActiveBomItems,
  type BomItem,
  type EquipmentCatalogEntry,
  type SupplyChainPlan,
} from '../../types/supplyChain';
import { GENERATION_EQUIPMENT_CATALOG } from './generationEquipmentCatalog';
import {
  evaluateGridFrequencyFit,
  gridFrequencyCaveat,
  type SiteGridFrequencyHz,
} from './gridFrequency';
import {
  OEM_GENERATION_SOURCE_REGISTER,
  preferredOemPerformanceCase,
} from './generationOemSourceRegister';
import { assessOemSimulationAdmission } from './generationOemSimulationAdmissions';

export type GenerationEquipmentImportTier =
  | 'catalog-envelope'
  | 'prefill-full'
  | 'prefill-partial'
  | 'insufficient';

export interface GenerationUserEquipmentPrefill {
  sourceLabel: string;
  importSource: GenerationUserEquipmentImportSource;
  manufacturer?: string;
  model?: string;
  trainConfiguration?: string;
  technology?: GenerationUserEquipmentEntry['technology'];
  unitsPerBlock?: number;
  ratedOutputMWPerBlock?: number;
  ratingScope?: GenerationEquipmentRatingScope;
  /** Always kJ/kWh; the dialog's unit select prefills 'kJ/kWh'. */
  heatRateValueKjPerKwh?: number;
  /** null = value known but the OEM basis is 'not-stated' — force a choice. */
  heatingValueBasis?: 'LHV' | 'HHV' | null;
  /** True when the heat rate was derived (efficiency or fuel input), never published directly. */
  heatRateDerived?: boolean;
  /** Structured grid-frequency capability from the OEM case ([] = not stated). */
  frequencyHz?: readonly (50 | 60)[];
  /** Provenance + caveats, landed in the entry's notes. */
  notes: string;
  /** Fields the dialog must draw the user's attention to before submit. */
  promptFields: readonly ('heatingValueBasis' | 'heatRateValue' | 'technology')[];
}

export type GenerationEquipmentImportResolution =
  | {
      tier: 'catalog-envelope';
      catalogId: string;
      admission: 'apply-approved' | 'comparison-only';
      manufacturer: string;
      model: string;
      reason: string;
    }
  | { tier: 'prefill-full'; prefill: GenerationUserEquipmentPrefill; caveats: readonly string[] }
  | { tier: 'prefill-partial'; prefill: GenerationUserEquipmentPrefill; missing: readonly string[] }
  | {
      tier: 'insufficient';
      reasons: readonly string[];
      identityPrefill?: Pick<GenerationUserEquipmentPrefill,
        'manufacturer' | 'model' | 'notes' | 'sourceLabel' | 'importSource' | 'promptFields'>;
    };

const SHIPPED_CATALOG_IDS = new Set(GENERATION_EQUIPMENT_CATALOG.map(entry => entry.catalogId));

/** Turbine kinds screen as simple-cycle; engine kinds as reciprocating. The
 *  register's technology enum cannot distinguish a combined-cycle island, so
 *  a CC mapping is never automatic — CC products are the tier-a envelopes. */
export function mapOemTechnologyToUserTechnology(
  technology: OemGenerationTechnology | string,
): GenerationUserEquipmentEntry['technology'] | null {
  switch (technology) {
    case 'industrial-gas-turbine':
    case 'aeroderivative-gas-turbine':
    case 'microturbine':
      return 'simple-cycle-gas-turbine';
    case 'spark-ignited-gas-engine':
    case 'compression-ignition-engine':
    case 'dual-fuel-engine':
      return 'reciprocating-engine';
    default:
      return null;
  }
}

/** `engine-shaft` has no electrical screening meaning — deliberately null. */
export function mapOemRatingScope(
  scope: OemGenerationRatingScope | string,
): GenerationEquipmentRatingScope | null {
  switch (scope) {
    case 'package-net':
    case 'plant-net':
      return 'net-plant';
    case 'package-gross':
    case 'plant-gross':
      return 'gross-plant';
    case 'generator-terminals':
      return 'generator-terminals';
    default:
      return null;
  }
}

export interface HeatRatePrefillDerivation {
  valueKjPerKwh: number;
  /** null = the basis is not published — the dialog must force a choice. */
  basis: 'LHV' | 'HHV' | null;
  derived: boolean;
  derivation: string | null;
}

/**
 * Published heat rate first; otherwise a LABELED derivation from electrical
 * efficiency (360000 / η%) or fuel input (fuelInputKw / outputKw × 3600).
 * Returns null when nothing defensible exists — never a guess.
 */
export function deriveHeatRatePrefill(
  performance: OemGenerationPerformanceCase,
): HeatRatePrefillDerivation | null {
  if (performance.heatRate != null && Number.isFinite(performance.heatRate.valueKjPerKwh)) {
    const basis = performance.heatRate.heatingValueBasis;
    return {
      valueKjPerKwh: performance.heatRate.valueKjPerKwh,
      basis: basis === 'LHV' || basis === 'HHV' ? basis : null,
      derived: false,
      derivation: basis === 'not-stated'
        ? 'The OEM source does not state whether this heat rate is LHV or HHV.'
        : null,
    };
  }
  if (performance.electricalEfficiencyPct != null
    && performance.electricalEfficiencyPct > 0
    && performance.electricalEfficiencyPct < 100) {
    return {
      valueKjPerKwh: 360_000 / performance.electricalEfficiencyPct,
      basis: null,
      derived: true,
      derivation: `Heat rate derived from published electrical efficiency (360,000 / ${performance.electricalEfficiencyPct.toFixed(1)}%); heating-value basis not stated by the OEM.`,
    };
  }
  if (performance.fuelInputKw != null
    && performance.fuelInputKw > 0
    && performance.electricalOutputKw > 0) {
    return {
      valueKjPerKwh: (performance.fuelInputKw / performance.electricalOutputKw) * 3_600,
      basis: null,
      derived: true,
      derivation: 'Heat rate derived from published fuel input ÷ electrical output; heating-value basis not stated by the OEM.',
    };
  }
  return null;
}

export interface ResolveOemProductImportOptions {
  register?: OemGenerationSourceRegister;
  performanceCaseId?: string;
  /**
   * Site grid frequency for mismatch caveats (see `gridFrequency.ts`).
   * Omitted/null = unknown site: the mismatch check is suppressed, never
   * defaulted.
   */
  siteGridFrequencyHz?: SiteGridFrequencyHz | null;
}

export function resolveOemProductImport(
  productId: string,
  options: ResolveOemProductImportOptions = {},
): GenerationEquipmentImportResolution {
  const register = options.register ?? OEM_GENERATION_SOURCE_REGISTER;
  const product = register.products.find(candidate => candidate.id === productId);
  if (!product) {
    return {
      tier: 'insufficient',
      reasons: [`OEM product "${productId}" is not in the source register.`],
    };
  }
  const performance = options.performanceCaseId != null
    ? product.performanceCases.find(candidate => candidate.id === options.performanceCaseId)
    : preferredOemPerformanceCase(product);
  if (!performance) {
    return {
      tier: 'insufficient',
      reasons: [`Performance case "${options.performanceCaseId}" is not published for ${product.manufacturer} ${product.model}.`],
    };
  }

  // Tier a — an exact reviewed admission (or a governed envelope link) means
  // the REAL shipped envelope carries this product's screening duty.
  const admission = assessOemSimulationAdmission(product.id, performance.id, 'generation-impact');
  const admittedTarget = admission.admissions.find(
    row => row.targetComponent === 'generation-impact-envelope'
      && SHIPPED_CATALOG_IDS.has(row.targetId),
  );
  if (admittedTarget) {
    return {
      tier: 'catalog-envelope',
      catalogId: admittedTarget.targetId,
      admission: admission.status === 'apply-approved' ? 'apply-approved' : 'comparison-only',
      manufacturer: product.manufacturer,
      model: product.model,
      reason: admission.reason,
    };
  }
  if (product.simulationCatalogId != null && SHIPPED_CATALOG_IDS.has(product.simulationCatalogId)) {
    // Fail-closed safety net: a governed envelope exists but no reviewed
    // admission for this case — offer the envelope for comparison only.
    return {
      tier: 'catalog-envelope',
      catalogId: product.simulationCatalogId,
      admission: 'comparison-only',
      manufacturer: product.manufacturer,
      model: product.model,
      reason: 'A governed screening envelope exists for this product, but this performance case has no reviewed simulation admission.',
    };
  }

  const importSource: GenerationUserEquipmentImportSource = {
    kind: 'oem-register',
    oemProductId: product.id,
    oemPerformanceCaseId: performance.id,
    label: `OEM database · ${product.manufacturer} ${product.model} · ${performance.label}`,
  };
  const sourceLabel = `OEM database · ${product.manufacturer} ${product.model}`;

  const ratingScope = mapOemRatingScope(performance.ratingScope);
  if (ratingScope == null) {
    return {
      tier: 'insufficient',
      reasons: [
        `The published rating is ${performance.ratingScope.replaceAll('-', ' ')} — it cannot be screened without a generator-terminal, package, or plant electrical rating.`,
      ],
      identityPrefill: {
        manufacturer: product.manufacturer,
        model: product.model,
        sourceLabel,
        importSource,
        promptFields: ['heatRateValue', 'heatingValueBasis'],
        notes: `Imported identity from the OEM database (${performance.label}); the ${performance.ratingScope} rating is not screenable — enter an electrical rating from the datasheet.`,
      },
    };
  }

  const technology = mapOemTechnologyToUserTechnology(product.technology);
  const caveats: string[] = [];
  const promptFields: GenerationUserEquipmentPrefill['promptFields'][number][] = [];
  if (product.technology === 'microturbine') {
    caveats.push('Microturbines screen under the simple-cycle planning archetype; confirm the technology choice.');
    promptFields.push('technology');
  }

  const frequencyFit = evaluateGridFrequencyFit(
    performance.frequencyHz,
    options.siteGridFrequencyHz ?? null,
  );
  const frequencyCaveat = gridFrequencyCaveat(
    frequencyFit,
    performance.frequencyHz,
    options.siteGridFrequencyHz ?? null,
  );
  if (frequencyCaveat) caveats.push(frequencyCaveat);

  const heatRate = deriveHeatRatePrefill(performance);
  const noteParts = [
    `Imported from the OEM database: ${product.manufacturer} ${product.model} (${product.variant}), case "${performance.label}" — ${performance.electricalOutputKw.toLocaleString('en-US')} kW ${performance.ratingScope.replaceAll('-', ' ')}, ${performance.referenceConditions}`,
    `Sources: ${performance.sourceIds.join(', ')}.`,
  ];
  if (heatRate?.derivation) {
    noteParts.push(heatRate.derivation);
    caveats.push(heatRate.derivation);
  }
  if (frequencyCaveat) noteParts.push(frequencyCaveat);
  if (heatRate != null && heatRate.basis == null) promptFields.push('heatingValueBasis');

  const basePrefill: GenerationUserEquipmentPrefill = {
    sourceLabel,
    importSource,
    manufacturer: product.manufacturer,
    model: product.model,
    trainConfiguration: performance.frequencyHz.length > 0
      ? `${product.variant || performance.label} · ${performance.frequencyHz.join('/')} Hz`
      : (product.variant || performance.label),
    ...(technology ? { technology } : {}),
    unitsPerBlock: 1,
    ratedOutputMWPerBlock: performance.electricalOutputKw / 1_000,
    ratingScope,
    frequencyHz: performance.frequencyHz,
    notes: noteParts.join(' '),
    promptFields,
  };

  if (heatRate == null) {
    return {
      tier: 'prefill-partial',
      prefill: { ...basePrefill, promptFields: [...promptFields, 'heatRateValue', 'heatingValueBasis'] },
      missing: ['heat rate (no published heat rate, efficiency, or fuel input)'],
    };
  }
  return {
    tier: 'prefill-full',
    prefill: {
      ...basePrefill,
      heatRateValueKjPerKwh: heatRate.valueKjPerKwh,
      heatingValueBasis: heatRate.basis,
      heatRateDerived: heatRate.derived,
    },
    caveats,
  };
}

export interface ResolveBomItemImportOptions {
  register?: OemGenerationSourceRegister;
  /** See {@link ResolveOemProductImportOptions.siteGridFrequencyHz}. */
  siteGridFrequencyHz?: SiteGridFrequencyHz | null;
}

export function resolveBomItemImport(
  item: BomItem,
  options: ResolveBomItemImportOptions = {},
): GenerationEquipmentImportResolution {
  const snapshot = item.oemGenerationBasis;
  const bomLabel = `Equipment BOM · "${item.name}" (qty ${item.quantity})`;
  if (snapshot?.productId) {
    const resolved = resolveOemProductImport(snapshot.productId, {
      ...(options.register ? { register: options.register } : {}),
      ...(options.siteGridFrequencyHz !== undefined
        ? { siteGridFrequencyHz: options.siteGridFrequencyHz }
        : {}),
      performanceCaseId: snapshot.performanceCaseId,
    });
    // Re-attribute prefills to the BOM line so coverage tracking works.
    if (resolved.tier === 'prefill-full' || resolved.tier === 'prefill-partial') {
      const prefill: GenerationUserEquipmentPrefill = {
        ...resolved.prefill,
        sourceLabel: bomLabel,
        importSource: {
          kind: 'equipment-bom',
          bomItemId: item.id,
          oemProductId: snapshot.productId,
          oemPerformanceCaseId: snapshot.performanceCaseId,
          label: bomLabel,
        },
        notes: `${resolved.prefill.notes} BOM line: "${item.name}" (qty ${item.quantity}).`,
      };
      return resolved.tier === 'prefill-full'
        ? { tier: 'prefill-full', prefill, caveats: resolved.caveats }
        : { tier: 'prefill-partial', prefill, missing: resolved.missing };
    }
    if (resolved.tier === 'catalog-envelope') return resolved;
    // Stale snapshot (product left the register): fall through to BOM fields.
  }

  const importSource: GenerationUserEquipmentImportSource = {
    kind: 'equipment-bom',
    bomItemId: item.id,
    label: bomLabel,
  };
  if (item.powerPerUnitMw == null || !Number.isFinite(item.powerPerUnitMw) || item.powerPerUnitMw <= 0) {
    return {
      tier: 'insufficient',
      reasons: ['No per-unit MW on this BOM line — add powerPerUnitMw in the Equipment BOM or enter the rating manually.'],
      identityPrefill: {
        ...(snapshot?.manufacturer ? { manufacturer: snapshot.manufacturer } : {}),
        model: snapshot?.model ?? item.name,
        sourceLabel: bomLabel,
        importSource,
        promptFields: ['heatRateValue', 'heatingValueBasis', 'technology'],
        notes: `Imported identity from the Equipment BOM line "${item.name}" (qty ${item.quantity}); no per-unit rating or performance data on the line.`,
      },
    };
  }

  const technology = snapshot?.technology != null
    ? mapOemTechnologyToUserTechnology(snapshot.technology)
    : null;
  return {
    tier: 'prefill-partial',
    prefill: {
      sourceLabel: bomLabel,
      importSource,
      ...(snapshot?.manufacturer ? { manufacturer: snapshot.manufacturer } : {}),
      model: snapshot?.model ?? item.name,
      ...(item.spec ? { trainConfiguration: item.spec } : {}),
      ...(technology ? { technology } : {}),
      // One G&I block = the N-1 contingency element = one procured unit. The
      // BOM quantity is a fleet-count decision for the Capacity stage, never
      // folded into a single block.
      unitsPerBlock: 1,
      ratedOutputMWPerBlock: item.powerPerUnitMw,
      // BOM powerPerUnitMw is documented as NET per-unit nameplate MW.
      ratingScope: 'net-plant',
      notes: `Imported from Equipment BOM line "${item.name}" (qty ${item.quantity} × ${item.powerPerUnitMw} MW net per unit). BOM MW is net per-unit nameplate; heat rate, auxiliary, and ambient data are not on the BOM — request OEM data. Set the fleet block count at the Capacity stage.`,
      promptFields: technology
        ? ['heatRateValue', 'heatingValueBasis']
        : ['heatRateValue', 'heatingValueBasis', 'technology'],
    },
    missing: ['heat rate', ...(technology ? [] : ['technology'])],
  };
}

/** Route a platform/company equipment-catalog row through the same ladder. */
export function resolveEquipmentCatalogEntryImport(
  entry: EquipmentCatalogEntry,
  options: ResolveOemProductImportOptions = {},
): GenerationEquipmentImportResolution {
  if (entry.generationScreeningId != null && SHIPPED_CATALOG_IDS.has(entry.generationScreeningId)) {
    const shipped = GENERATION_EQUIPMENT_CATALOG.find(
      candidate => candidate.catalogId === entry.generationScreeningId,
    )!;
    return {
      tier: 'catalog-envelope',
      catalogId: shipped.catalogId,
      admission: shipped.confirmationRequired ? 'comparison-only' : 'apply-approved',
      manufacturer: shipped.manufacturer,
      model: shipped.model,
      reason: 'This catalog row is the browsable face of a governed screening envelope.',
    };
  }
  if (entry.oemGenerationProductId != null) {
    return resolveOemProductImport(entry.oemGenerationProductId, {
      ...options,
      ...(entry.oemPerformanceCaseId != null
        ? { performanceCaseId: entry.oemPerformanceCaseId }
        : {}),
    });
  }
  const label = `Company catalog · "${entry.name}"`;
  const importSource: GenerationUserEquipmentImportSource = {
    kind: 'equipment-catalog',
    catalogEntryId: entry.id,
    label,
  };
  if (entry.powerPerUnitMw != null && Number.isFinite(entry.powerPerUnitMw) && entry.powerPerUnitMw > 0) {
    return {
      tier: 'prefill-partial',
      prefill: {
        sourceLabel: label,
        importSource,
        model: entry.name,
        ...(entry.spec ? { trainConfiguration: entry.spec } : {}),
        unitsPerBlock: 1,
        ratedOutputMWPerBlock: entry.powerPerUnitMw,
        ratingScope: 'net-plant',
        notes: `Imported from the equipment catalog entry "${entry.name}" (${entry.powerPerUnitMw} MW net per unit). Heat rate and derate data are not on the catalog row — request OEM data.`,
        promptFields: ['heatRateValue', 'heatingValueBasis', 'technology'],
      },
      missing: ['heat rate', 'technology'],
    };
  }
  return {
    tier: 'insufficient',
    reasons: ['This catalog entry carries no per-unit MW or OEM product link — enter the rating manually.'],
    identityPrefill: {
      model: entry.name,
      sourceLabel: label,
      importSource,
      promptFields: ['heatRateValue', 'heatingValueBasis', 'technology'],
      notes: `Imported identity from the equipment catalog entry "${entry.name}"; no performance data on the row.`,
    },
  };
}

/** Active generation BOM lines (option groups collapsed); [] without a plan. */
export function listGenerationBomImportCandidates(
  plan: SupplyChainPlan | null | undefined,
): readonly BomItem[] {
  if (!plan || !Array.isArray(plan.bom) || plan.bom.length === 0) return [];
  return selectActiveBomItems(plan.bom).filter(isGenerationBomItem);
}

export interface GenerationBomEquipmentCoverage {
  generationLineCount: number;
  unrepresented: readonly {
    bomItemId: string;
    name: string;
    tier: GenerationEquipmentImportTier;
  }[];
}

function normalizedIdentity(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * Which active generation BOM lines are NOT yet represented in the equipment
 * working set. Represented = the line's admitted envelope is shortlisted or
 * applied, OR a user entry was imported from the line, OR a user entry matches
 * its OEM identity (manufacturer + model heuristic fallback).
 */
export function summarizeBomEquipmentCoverage(
  plan: SupplyChainPlan | null | undefined,
  workspace: GenerationEquipmentWorkspace | undefined,
  appliedCatalogId?: string,
): GenerationBomEquipmentCoverage {
  const candidates = listGenerationBomImportCandidates(plan);
  const shortlisted = new Set(workspace?.shortlistedCatalogIds ?? []);
  if (appliedCatalogId) shortlisted.add(appliedCatalogId);
  const userEntries = workspace?.userEquipment ?? [];

  const unrepresented: Array<GenerationBomEquipmentCoverage['unrepresented'][number]> = [];
  for (const item of candidates) {
    const resolution = resolveBomItemImport(item);
    const representedByEnvelope = resolution.tier === 'catalog-envelope'
      && shortlisted.has(resolution.catalogId);
    const representedByImport = userEntries.some(
      entry => entry.importSource?.bomItemId === item.id,
    );
    const snapshot = item.oemGenerationBasis;
    const representedByIdentity = userEntries.some(entry => (
      snapshot != null
        ? normalizedIdentity(entry.manufacturer) === normalizedIdentity(snapshot.manufacturer)
          && normalizedIdentity(entry.model) === normalizedIdentity(snapshot.model)
        : normalizedIdentity(entry.model) === normalizedIdentity(item.name)
    ));
    if (!representedByEnvelope && !representedByImport && !representedByIdentity) {
      unrepresented.push({ bomItemId: item.id, name: item.name, tier: resolution.tier });
    }
  }
  return { generationLineCount: candidates.length, unrepresented };
}
